import React, { useState, useEffect } from "react";
import {
  Users,
  ArrowRight,
  Network,
  Zap,
  Brain,
  Globe,
  MessageSquare,
  Target,
  Smartphone,
  Download,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import logoIcon from "./assets/logoIcon.png";
import textLogo from "./assets/Text-Logo.png";

const EdgeCityLanding = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentSlide, setCurrentSlide] = useState(0);

  const museImages = [
    {
      src: "/muse-1.png",
      caption:
        "Sign in with your Edge City account to access Muse - your AI companion for gatherings",
    },
    {
      src: "/muse-2.png",
      caption:
        "Access your Edge City folder directly from Muse - everything in one place",
    },
    {
      src: "/muse-3.png",
      caption:
        "Share content in the Edge City folder - reports, uploads, and captures available to all participants",
    },
    {
      src: "/muse-4.png",
      caption:
        "All shared content becomes part of the collective memory, accessible to Eddie AI and fellow participants",
    },
    {
      src: "/muse-5.png",
      caption:
        "Browse the magic library, distilled from content uploaded by other users - never miss important discussions or insights from your gathering",
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % museImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + museImages.length) % museImages.length,
    );
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#4473b9]">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#282F4B] via-[#4473b9] to-[#4473b9] opacity-90" />

      {/* Magical background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs */}
        <div
          className="absolute w-[800px] h-[800px] rounded-full blur-3xl opacity-30 animate-float"
          style={{
            background:
              "linear-gradient(45deg, rgba(125, 176, 210, 0.6), rgba(69, 115, 185, 0.4))",
            transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`,
            top: "-20%",
            right: "-10%",
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 animate-float-reverse"
          style={{
            background:
              "linear-gradient(45deg, rgba(40, 47, 75, 0.4), rgba(125, 176, 210, 0.3))",
            transform: `translate(${mousePosition.x * -20}px, ${mousePosition.y * -20}px)`,
            bottom: "-10%",
            left: "-10%",
          }}
        />

        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0.2,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative flex flex-col items-center justify-center px-3 sm:px-4 min-h-screen py-12 sm:py-0">
        <div className="text-center w-full max-w-3xl mx-auto backdrop-blur-sm rounded-xl p-3 sm:p-8 bg-[#282F4B]/10">
          {/* Logo Section */}
          <a href="https://tinycloud.xyz">
            <div className="flex justify-center items-center gap-2 mb-4 sm:mb-6 group">
              <div className="relative transform hover:scale-110 transition-all duration-300">
                <img
                  src={logoIcon}
                  alt="TinyCloud Icon"
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                />
              </div>
              <img
                src={textLogo}
                alt="TinyCloud"
                className="h-5 sm:h-6 object-contain"
              />
            </div>
          </a>

          {/* Headline - Tagline */}
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight tracking-tight px-2">
            <span className="text-[#FDF9D2]">Edge City</span>
          </h1>

          {/* One-sentence claim */}
          <p className="font-space-grotesk text-xl sm:text-2xl md:text-3xl text-[#FDF9D2] mb-8 sm:mb-10 max-w-2xl mx-auto leading-snug sm:leading-relaxed px-2 sm:px-4 font-medium">
            AI for Gatherings - Remember everything, even if you're not there!
          </p>

          {/* Longer explanation */}
          <p className="font-space-grotesk text-base sm:text-lg md:text-xl text-[#FDF9D2]/90 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            Minimize FOMO and maximize connection at gatherings. Muse captures
            conversations, talks, and insights automatically, creating a shared
            memory that persists beyond the event. Access everything through
            Eddie, your AI companion who knows about every discussion,
            presentation, and connection made.
            <br />
            <br />
            Never miss important moments again - whether you're at every session
            or joining remotely, you'll have complete context of your gathering
            experience.
          </p>

          {/* Download Buttons */}
          <div className="w-full max-w-xl mx-auto mb-8 sm:mb-10 px-2">
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
              <a
                href="https://testflight.apple.com/join/UrtvpNMn"
                className="w-full sm:w-auto px-6 py-3 bg-[#4473b9] hover:bg-[#4473b9]/90 text-[#282F4B] font-bold rounded-lg flex items-center justify-center gap-3 transition-all hover:scale-105 text-lg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Smartphone className="h-5 w-5" />
                Download Muse for iOS
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="https://forms.gle/z6uyDXRSQksZRpZL6"
                className="w-full sm:w-auto px-6 py-3 bg-[#4473b9]/80 hover:bg-[#4473b9]/70 text-[#282F4B] font-bold rounded-lg flex items-center justify-center gap-3 transition-all hover:scale-105 text-lg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="h-5 w-5" />
                Android Beta
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
            <p className="text-center text-sm text-[#FDF9D2]/70 mb-4">
              Android is in closed beta - we'd love testers to sign up!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a
                href="https://t.me/edge_esmeralda_ai_bot"
                className="px-5 py-2 bg-[#4473b9]/90 hover:bg-[#4473b9] text-[#282F4B] font-medium rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-105 text-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Brain className="h-4 w-4" />
                Chat with Eddie AI
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="https://t.me/+pplkv1XbbU01MDVh"
                className="px-5 py-2 bg-[#4473b9]/70 hover:bg-[#4473b9]/80 text-[#282F4B] font-medium rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-105 text-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="h-4 w-4" />
                Join TinyCloud Telegram
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Muse Slideshow */}
          <div className="mb-16 sm:mb-20 px-4">
            <h2 className="text-xl sm:text-2xl font-bold text-[#FDF9D2] mb-6 text-center">
              How Muse Works at Gatherings
            </h2>
            <div className="relative max-w-4xl mx-auto">
              <div className="relative bg-[#282F4B]/20 rounded-lg p-4 sm:p-6 backdrop-blur-sm border border-[#4473b9]/20">
                <div className="flex items-center justify-center mb-4">
                  <img
                    src={museImages[currentSlide].src}
                    alt={`Muse screenshot ${currentSlide + 1}`}
                    className="max-w-full max-h-96 object-contain rounded-lg shadow-lg"
                  />
                </div>
                <p className="text-[#FDF9D2]/90 text-center mb-4 text-sm sm:text-base leading-relaxed">
                  {museImages[currentSlide].caption}
                </p>
                <div className="flex justify-between items-center">
                  <button
                    onClick={prevSlide}
                    className="p-2 rounded-full bg-[#4473b9]/20 hover:bg-[#4473b9]/40 transition-all"
                  >
                    <ChevronLeft className="h-6 w-6 text-[#4473b9]" />
                  </button>
                  <div className="flex space-x-2">
                    {museImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentSlide
                            ? "bg-[#4473b9]"
                            : "bg-[#4473b9]/30"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextSlide}
                    className="p-2 rounded-full bg-[#4473b9]/20 hover:bg-[#4473b9]/40 transition-all"
                  >
                    <ChevronRight className="h-6 w-6 text-[#4473b9]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 mb-16 sm:mb-20 px-4 sm:px-6">
            {[
              {
                icon: Brain,
                title: "Automatic Capture & Memory",
                desc: "Muse automatically captures conversations, talks, and discussions at your gathering, creating a persistent memory that never forgets.",
              },
              {
                icon: Network,
                title: "Shared Gathering Intelligence",
                desc: "All participants contribute to and access the same knowledge base - Eddie AI knows everything that happened, even if you missed it.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="flex flex-col items-center group p-6 sm:p-8 bg-[#282F4B]/10 rounded-lg backdrop-blur-sm border border-[#4473b9]/20 hover:border-[#4473b9]/40 hover:bg-[#282F4B]/20 transition-all duration-300"
              >
                <div className="relative mb-4 sm:mb-5">
                  <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#4473b9]" />
                  <div className="absolute inset-0 animate-ping opacity-50 group-hover:opacity-100">
                    <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#4473b9]" />
                  </div>
                </div>
                <h3 className="font-medium text-[#FDF9D2] mb-3 text-lg sm:text-xl">
                  {title}
                </h3>
                <p className="text-sm sm:text-base text-[#FDF9D2]/80 text-center leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>

          {/* How It Works Section */}
          <div className="mb-16 sm:mb-20 px-4">
            <h2 className="text-xl sm:text-2xl font-bold text-[#FDF9D2] mb-6">
              How AI for Gatherings Works
            </h2>
            <div className="flex flex-col space-y-4">
              <div className="bg-[#282F4B]/20 rounded-lg p-4 sm:p-6 text-left">
                <h3 className="text-lg font-semibold text-[#4473b9] mb-2 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Capture Everything
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base">
                  Attendees share conversations, upload content, and capture
                  insights directly into the shared Edge City folder through
                  Muse, creating a comprehensive record of the gathering.
                </p>
              </div>
              <div className="bg-[#282F4B]/20 rounded-lg p-4 sm:p-6 text-left">
                <h3 className="text-lg font-semibold text-[#4473b9] mb-2 flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  AI Understanding
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base">
                  Eddie AI processes all shared content, understanding context,
                  connections, and key insights from every conversation and
                  presentation at your gathering.
                </p>
              </div>
              <div className="bg-[#282F4B]/20 rounded-lg p-4 sm:p-6 text-left">
                <h3 className="text-lg font-semibold text-[#4473b9] mb-2 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Zero FOMO Access
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base">
                  Access everything through Eddie - ask about any discussion,
                  find specific insights, or catch up on what you missed. The
                  complete gathering experience, available anytime.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Links */}
          <div className="flex justify-center gap-4 text-sm">
            <a
              href="/"
              className="text-[#FDF9D2]/60 hover:text-[#FDF9D2]/80 underline"
            >
              Back to TinyCloud
            </a>
            <a
              href="/privacy"
              className="text-[#FDF9D2]/60 hover:text-[#FDF9D2]/80 underline"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EdgeCityLanding;
